import { useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { RefreshCw, Home, Zap } from 'lucide-react'
import { isChunkLoadError } from '../utils/isChunkLoadError'

// Per HARD GATE 5 (CLAUDE.md): пользователь НИКОГДА не должен вручную чистить
// PWA-кэш после деплоя. Когда lazyWithRetry circuit-breaker сработал и мы
// оказались здесь с chunk-error'ом, автоматически сносим SW + caches и
// перезагружаемся с cache-bust. Счётчик в sessionStorage защищает от
// бесконечного цикла: после AUTO_RECOVER_MAX попыток показываем ручную
// кнопку как break-glass.
const AUTO_RECOVER_KEY = 'onboardix_chunk_autorecover_count'
const AUTO_RECOVER_MAX = 2

async function autoRecoverFromStaleChunk() {
    try {
        if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations()
            await Promise.all(regs.map(r => r.unregister()))
        }
        if ('caches' in window) {
            const keys = await caches.keys()
            await Promise.all(keys.map(k => caches.delete(k)))
        }
    } catch { /* best effort */ }

    // Сбрасываем lazyWithRetry-счётчики: после SW+cache nuke он должен
    // получить чистый старт, иначе его circuit-breaker сработает снова.
    try {
        sessionStorage.removeItem('onboardix_chunk_reload_attempted')
        sessionStorage.removeItem('onboardix_chunk_reload_ts')
        sessionStorage.removeItem('onboardix_chunk_reload_count')
        sessionStorage.removeItem('onboardix_chunk_reload_window_start')
    } catch { /* storage disabled */ }

    const url = new URL(window.location.href)
    url.searchParams.delete('_v')
    url.searchParams.append('_v', String(Date.now()))
    window.location.href = url.toString()
}

export default function PageErrorBoundary({ children }) {
    const location = useLocation()
    const navigate = useNavigate()
    const [hasError, setHasError] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [autoRecovering, setAutoRecovering] = useState(false)

    // Reset error state when location changes (user navigates away)
    useEffect(() => {
        if (hasError) {
            setHasError(false)
            setErrorMsg('')
            setAutoRecovering(false)
        }
    }, [location.pathname]) // Only run when pathname changes

    const isStaleChunk = errorMsg?.includes('устаревшая версия')
    const attempts = (() => {
        try { return Number(sessionStorage.getItem(AUTO_RECOVER_KEY) || '0') } catch { return 0 }
    })()

    // Auto-recover от stale-chunk без клика пользователя. Пока не исчерпали
    // лимит попыток — сами нокаем SW + caches + reload с cache-bust.
    useEffect(() => {
        if (!hasError || !isStaleChunk || autoRecovering) return
        if (attempts >= AUTO_RECOVER_MAX) return
        try { sessionStorage.setItem(AUTO_RECOVER_KEY, String(attempts + 1)) } catch { /* noop */ }
        setAutoRecovering(true)
        void autoRecoverFromStaleChunk()
    }, [hasError, isStaleChunk, autoRecovering, attempts])

    // We still need a class component at the core to catch rendering errors,
    // so we wrap the children in an inner class component that reports back.
    return (
        <ErrorBoundaryCore
            hasError={hasError}
            setHasError={setHasError}
            setErrorMsg={setErrorMsg}
        >
            {hasError ? (
                <div
                    style={{
                        padding: '40px 24px',
                        background: 'var(--color-bg-elevated)',
                        fontFamily: 'system-ui, sans-serif'
                    }}
                    className='text-center h-full flex flex-col items-center justify-center'>
                    <div
                        style={{
                            fontSize: 28
                        }}
                        className='mb-16'>{autoRecovering ? '↻' : '🔌'}</div>
                    <h2
                        style={{
                            margin: '0 0 12px 0'
                        }}
                        className='text-3xl fw-700 color-text'>
                        {autoRecovering ? 'Обновляем приложение…' : 'Сбой загрузки модуля'}
                    </h2>
                    <p
                        style={{
                            maxWidth: 400,
                            margin: '0 0 24px 0',
                            lineHeight: 1.5
                        }}
                        className='text-lg text-muted'>
                        {autoRecovering
                            ? 'Загружаем свежую версию интерфейса. Страница перезагрузится через секунду.'
                            : 'В этом разделе произошла ошибка. Возможно, у вас пропало соединение с сетью или часть данных недоступна в офлайн-режиме.'}
                    </p>
                    {!autoRecovering && (
                        <>
                            <div
                                style={{
                                    background: 'var(--color-bg-card)',
                                    border: '1px solid var(--color-border)',
                                    maxWidth: 500,
                                    wordBreak: 'break-all',
                                    textAlign: 'left',
                                    fontFamily: 'monospace'
                                }}
                                className='p-12 radius-8 text-xs color-danger mb-24'>
                                {errorMsg || 'Unknown rendering error'}
                            </div>
                            <div className="flex-gap-12">
                                {isStaleChunk ? (
                                    <button
                                        onClick={() => {
                                            // Break-glass: ручной вызов того же hard-reset.
                                            // Сбрасываем счётчик автовосстановления, чтобы
                                            // после клика авто-рекавер снова мог сработать.
                                            try { sessionStorage.removeItem(AUTO_RECOVER_KEY) } catch { /* noop */ }
                                            setAutoRecovering(true)
                                            void autoRecoverFromStaleChunk()
                                        }}
                                        className='radius-8 color-white text-md fw-600 cursor-pointer flex items-center gap-6'
                                        style={{
                                            padding: '8px 20px',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, var(--color-success), var(--color-success))'
                                        }}
                                    >
                                        <Zap size={14} /> Загрузить обновление
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => { setHasError(false); setErrorMsg('') }}
                                        className='radius-8 color-white text-md fw-600 cursor-pointer flex items-center gap-6'
                                        style={{
                                            padding: '8px 20px',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent))'
                                        }}
                                    >
                                        <RefreshCw size={14} /> Попробовать снова
                                    </button>
                                )}
                                <button
                                    onClick={() => { setHasError(false); navigate('/') }}
                                    className='radius-8 color-text text-md fw-600 cursor-pointer flex items-center gap-6'
                                    style={{
                                        padding: '8px 20px',
                                        border: '1px solid var(--color-border)',
                                        background: 'transparent'
                                    }}
                                >
                                    <Home size={14} /> На главную
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                children
            )}
        </ErrorBoundaryCore>
    );
}

import React from 'react'
class ErrorBoundaryCore extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true }
    }

    componentDidUpdate(prevProps) {
        // Parent reset the error (user navigated away) — clear internal state
        // so that children can render again on the new route.
        if (prevProps.hasError && !this.props.hasError) {
            this.setState({ hasError: false })
        }
    }

    componentDidCatch(error, errorInfo) {
        console.error('[PageErrorBoundary] Caught error:', error, errorInfo)
        this.props.setHasError(true)
        const stack = (errorInfo?.componentStack || '').trim()

        let msg = error.toString()

        if (isChunkLoadError(error)) {
             msg = "Обнаружена устаревшая версия компонента интерфейса. Для работы этой страницы требуется загрузить актуальное обновление."
        } else {
             msg = stack ? `${msg}\n\n— component stack —${stack.slice(0, 1200)}${stack.length > 1200 ? '…' : ''}` : msg
        }

        this.props.setErrorMsg(msg)
    }

    render() {
        if (this.state.hasError && !this.props.hasError) {
            // Error caught, but parent state not yet updated — return null briefly
            return null
        }
        // No error: renders page children
        // Error + parent updated (props.hasError=true): renders error UI children
        return this.props.children
    }
}
